import { useState } from 'react';
import { Stack, Paper, TextInput, Group, Select, Text, Skeleton, Tabs, Badge } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { DataTable, type DataTableSortStatus } from 'mantine-datatable';
import {
  useGoogleAdsKeywords,
  useGoogleAdsNegativeKeywords,
  useGoogleAdsSearchTerms,
  useGoogleAdsAccount,
} from '@/hooks/useGoogleAds';
import { microsToAmount, formatPercentage } from '@/types/googleAds';
import type { DateRangeOption, Keyword, NegativeKeyword, SearchTerm } from '@/types/googleAds';
import { StatusBadge } from '../components/StatusBadge';

interface KeywordsTabProps {
  customerId: string;
  dateRange: DateRangeOption;
}

export function KeywordsTab({ customerId, dateRange }: KeywordsTabProps) {
  const { data: keywords, isLoading: keywordsLoading } = useGoogleAdsKeywords(customerId);
  const { data: negativeKeywords, isLoading: negativeLoading } = useGoogleAdsNegativeKeywords(customerId);
  const { data: searchTerms, isLoading: searchTermsLoading } = useGoogleAdsSearchTerms(customerId, dateRange);
  const { data: account } = useGoogleAdsAccount(customerId);

  const [activeTab, setActiveTab] = useState<string | null>('keywords');
  const [search, setSearch] = useState('');
  const [matchTypeFilter, setMatchTypeFilter] = useState('');

  const [keywordSort, setKeywordSort] = useState<DataTableSortStatus<Keyword>>({
    columnAccessor: 'metrics.costMicros',
    direction: 'desc',
  });

  const [searchTermSort, setSearchTermSort] = useState<DataTableSortStatus<SearchTerm>>({
    columnAccessor: 'metrics.costMicros',
    direction: 'desc',
  });

  const formatCurrency = (micros: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: account?.currency ?? 'EUR' }).format(
      microsToAmount(micros),
    );

  const formatNumber = (num: number) => new Intl.NumberFormat('de-DE').format(num);

  const filteredKeywords = keywords
    ?.filter((k) => {
      if (search && !k.text.toLowerCase().includes(search.toLowerCase())) return false;
      if (matchTypeFilter && k.matchType !== matchTypeFilter) return false;
      return true;
    })
    .sort((a, b) => {
      const accessor = keywordSort.columnAccessor;
      let aVal: number | string | null;
      let bVal: number | string | null;

      if (accessor.startsWith('metrics.')) {
        const key = accessor.replace('metrics.', '') as keyof Keyword['metrics'];
        aVal = a.metrics[key];
        bVal = b.metrics[key];
      } else if (accessor === 'qualityScore') {
        aVal = a.qualityScore;
        bVal = b.qualityScore;
      } else {
        aVal = a[accessor as keyof Keyword] as string;
        bVal = b[accessor as keyof Keyword] as string;
      }

      if (aVal === null) aVal = -1;
      if (bVal === null) bVal = -1;

      if (typeof aVal === 'string') {
        return keywordSort.direction === 'asc'
          ? aVal.localeCompare(bVal as string)
          : (bVal as string).localeCompare(aVal);
      }
      return keywordSort.direction === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

  const filteredNegativeKeywords = negativeKeywords?.filter((k) => {
    if (search && !k.text.toLowerCase().includes(search.toLowerCase())) return false;
    if (matchTypeFilter && k.matchType !== matchTypeFilter) return false;
    return true;
  });

  const filteredSearchTerms = searchTerms
    ?.filter((s) => {
      if (search && !s.searchTerm.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const accessor = searchTermSort.columnAccessor;
      let aVal: number;
      let bVal: number;

      if (accessor.startsWith('metrics.')) {
        const key = accessor.replace('metrics.', '') as keyof SearchTerm['metrics'];
        aVal = a.metrics[key];
        bVal = b.metrics[key];
      } else {
        return 0;
      }

      return searchTermSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

  const isLoading = keywordsLoading || negativeLoading || searchTermsLoading;

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={40} />
        <Skeleton height={400} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Paper withBorder p="md">
        <Group>
          <TextInput
            placeholder="Keyword suchen..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={250}
          />
          {activeTab !== 'searchTerms' && (
            <Select
              placeholder="Match Type"
              data={[
                { value: '', label: 'Alle Match Types' },
                { value: 'EXACT', label: 'Exact' },
                { value: 'PHRASE', label: 'Phrase' },
                { value: 'BROAD', label: 'Broad' },
              ]}
              value={matchTypeFilter}
              onChange={(v) => setMatchTypeFilter(v ?? '')}
              clearable
              w={150}
            />
          )}
        </Group>
      </Paper>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="keywords">
            Keywords{' '}
            <Badge size="sm" variant="light" ml={4}>
              {keywords?.length ?? 0}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="negative">
            Negative Keywords{' '}
            <Badge size="sm" variant="light" ml={4}>
              {negativeKeywords?.length ?? 0}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="searchTerms">
            Suchbegriffe{' '}
            <Badge size="sm" variant="light" ml={4}>
              {searchTerms?.length ?? 0}
            </Badge>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="keywords" pt="md">
          <DataTable
            withTableBorder
            borderRadius="sm"
            striped
            highlightOnHover
            records={filteredKeywords ?? []}
            sortStatus={keywordSort}
            onSortStatusChange={setKeywordSort}
            columns={[
              {
                accessor: 'text',
                title: 'Keyword',
                sortable: true,
                width: 250,
                render: (keyword) => (
                  <div>
                    <Text size="sm" fw={500}>
                      {keyword.text}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {keyword.campaignName} / {keyword.adGroupName}
                    </Text>
                  </div>
                ),
              },
              {
                accessor: 'matchType',
                title: 'Match',
                width: 80,
                render: (keyword) => (
                  <Badge variant="outline" size="sm">
                    {keyword.matchType}
                  </Badge>
                ),
              },
              {
                accessor: 'status',
                title: 'Status',
                width: 90,
                render: (keyword) => <StatusBadge status={keyword.status} />,
              },
              {
                accessor: 'qualityScore',
                title: 'QS',
                sortable: true,
                textAlign: 'center',
                width: 50,
                render: (keyword) => (
                  <Badge
                    color={
                      keyword.qualityScore === null
                        ? 'gray'
                        : keyword.qualityScore >= 7
                          ? 'green'
                          : keyword.qualityScore >= 4
                            ? 'yellow'
                            : 'red'
                    }
                    size="sm"
                  >
                    {keyword.qualityScore ?? '-'}
                  </Badge>
                ),
              },
              {
                accessor: 'expectedCtr',
                title: 'Exp. CTR',
                width: 110,
                render: (keyword) =>
                  keyword.expectedCtr ? <StatusBadge status={keyword.expectedCtr} type="performance" /> : '-',
              },
              {
                accessor: 'adRelevance',
                title: 'Relevanz',
                width: 110,
                render: (keyword) =>
                  keyword.adRelevance ? <StatusBadge status={keyword.adRelevance} type="performance" /> : '-',
              },
              {
                accessor: 'landingPageExperience',
                title: 'LP Exp.',
                width: 110,
                render: (keyword) =>
                  keyword.landingPageExperience ? (
                    <StatusBadge status={keyword.landingPageExperience} type="performance" />
                  ) : (
                    '-'
                  ),
              },
              {
                accessor: 'metrics.impressions',
                title: 'Impr.',
                sortable: true,
                textAlign: 'right',
                width: 90,
                render: (keyword) => formatNumber(keyword.metrics.impressions),
              },
              {
                accessor: 'metrics.clicks',
                title: 'Klicks',
                sortable: true,
                textAlign: 'right',
                width: 70,
                render: (keyword) => formatNumber(keyword.metrics.clicks),
              },
              {
                accessor: 'metrics.ctr',
                title: 'CTR',
                sortable: true,
                textAlign: 'right',
                width: 70,
                render: (keyword) => formatPercentage(keyword.metrics.ctr),
              },
              {
                accessor: 'metrics.costMicros',
                title: 'Kosten',
                sortable: true,
                textAlign: 'right',
                width: 90,
                render: (keyword) => formatCurrency(keyword.metrics.costMicros),
              },
              {
                accessor: 'metrics.conversions',
                title: 'Conv.',
                sortable: true,
                textAlign: 'right',
                width: 70,
                render: (keyword) => keyword.metrics.conversions.toFixed(1),
              },
            ]}
            noRecordsText="Keine Keywords gefunden"
            minHeight={400}
          />
        </Tabs.Panel>

        <Tabs.Panel value="negative" pt="md">
          <DataTable
            withTableBorder
            borderRadius="sm"
            striped
            highlightOnHover
            records={filteredNegativeKeywords ?? []}
            columns={[
              {
                accessor: 'text',
                title: 'Negatives Keyword',
                width: 300,
              },
              {
                accessor: 'matchType',
                title: 'Match Type',
                width: 100,
                render: (keyword) => (
                  <Badge variant="outline" size="sm">
                    {keyword.matchType}
                  </Badge>
                ),
              },
              {
                accessor: 'level',
                title: 'Ebene',
                width: 120,
                render: (keyword) => (
                  <Badge variant="light" size="sm">
                    {keyword.level}
                  </Badge>
                ),
              },
              {
                accessor: 'campaignName',
                title: 'Kampagne',
                render: (keyword) => keyword.campaignName ?? '-',
              },
              {
                accessor: 'adGroupName',
                title: 'Anzeigengruppe',
                render: (keyword) => keyword.adGroupName ?? '-',
              },
              {
                accessor: 'sharedSetName',
                title: 'Shared Set',
                render: (keyword) => keyword.sharedSetName ?? '-',
              },
            ]}
            noRecordsText="Keine negativen Keywords gefunden"
            minHeight={400}
          />
        </Tabs.Panel>

        <Tabs.Panel value="searchTerms" pt="md">
          <DataTable
            withTableBorder
            borderRadius="sm"
            striped
            highlightOnHover
            records={filteredSearchTerms ?? []}
            sortStatus={searchTermSort}
            onSortStatusChange={setSearchTermSort}
            columns={[
              {
                accessor: 'searchTerm',
                title: 'Suchbegriff',
                sortable: true,
                width: 300,
              },
              {
                accessor: 'keywordText',
                title: 'Keyword',
                width: 200,
                render: (st) => st.keywordText ?? '-',
              },
              {
                accessor: 'campaignName',
                title: 'Kampagne',
                width: 200,
              },
              {
                accessor: 'status',
                title: 'Status',
                width: 120,
                render: (st) => (
                  <Badge
                    color={
                      st.status === 'ADDED'
                        ? 'green'
                        : st.status === 'EXCLUDED'
                          ? 'red'
                          : st.status === 'ADDED_EXCLUDED'
                            ? 'yellow'
                            : 'gray'
                    }
                    variant="light"
                    size="sm"
                  >
                    {st.status === 'ADDED'
                      ? 'Hinzugefügt'
                      : st.status === 'EXCLUDED'
                        ? 'Ausgeschlossen'
                        : st.status === 'ADDED_EXCLUDED'
                          ? 'Beides'
                          : 'Keine Aktion'}
                  </Badge>
                ),
              },
              {
                accessor: 'metrics.impressions',
                title: 'Impr.',
                sortable: true,
                textAlign: 'right',
                width: 90,
                render: (st) => formatNumber(st.metrics.impressions),
              },
              {
                accessor: 'metrics.clicks',
                title: 'Klicks',
                sortable: true,
                textAlign: 'right',
                width: 70,
                render: (st) => formatNumber(st.metrics.clicks),
              },
              {
                accessor: 'metrics.ctr',
                title: 'CTR',
                sortable: true,
                textAlign: 'right',
                width: 70,
                render: (st) => formatPercentage(st.metrics.ctr),
              },
              {
                accessor: 'metrics.costMicros',
                title: 'Kosten',
                sortable: true,
                textAlign: 'right',
                width: 90,
                render: (st) => formatCurrency(st.metrics.costMicros),
              },
              {
                accessor: 'metrics.conversions',
                title: 'Conv.',
                sortable: true,
                textAlign: 'right',
                width: 70,
                render: (st) => st.metrics.conversions.toFixed(1),
              },
            ]}
            noRecordsText="Keine Suchbegriffe gefunden"
            minHeight={400}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
